/**
 * Workstream RESPOND page — what the recipient sees when they click the
 * email button.
 *
 * Route: /workstream/respond/:token
 *
 * Auth model: the URL token IS the credential. No PSI login required.
 * Works equally well for PSI users and external recipients (vendors,
 * partners, contractors).
 *
 * Three phases:
 *   1. Arrival splash — "You have N items waiting" + Continue button.
 *      This deliberate user click defeats Outlook Safelinks pre-fetch
 *      (which would otherwise pre-load the page and consume the token).
 *      No data is fetched until Continue is clicked.
 *   2. Items view — one card per item with inline actions.
 *   3. Done state — friendly completion after acknowledge-all.
 *
 * Design principles (per the competitive UX research):
 *   - One screen, one scroll, zero modals.
 *   - Per-item: Acknowledge / Mark done / Comment / Snooze / Open in PSI.
 *   - Bulk "Acknowledge all" sticky action bar.
 *   - Comments thread the recipient's reply back to the compliance team
 *     without forcing them into PSI.
 *   - Snooze with date + reason — kills the nag cycle that drives Vanta
 *     G2 complaints.
 *   - Light theme so this feels welcoming, not "internal compliance tool."
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || '';

export default function RespondPage() {
  const { token } = useParams();
  const [phase, setPhase] = useState('arrival');     // 'arrival' | 'loading' | 'items' | 'done' | 'error'
  const [outreach, setOutreach] = useState(null);
  const [items, setItems] = useState([]);
  const [responses, setResponses] = useState([]);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setPhase('loading');
    setError(null);
    try {
      const res = await fetch(`${API}/api/workstream/respond/${encodeURIComponent(token)}/load`, {
        method: 'GET',
        credentials: 'include',
      });
      const data = await res.json();
      if (!data.success) {
        setError(data.message || 'This link is no longer valid.');
        setPhase('error');
        return;
      }
      setOutreach(data.outreach);
      setItems(data.items || []);
      setResponses(data.responses || []);
      setPhase('items');
    } catch (e) {
      setError('Network error: ' + e.message);
      setPhase('error');
    }
  }, [token]);

  const refreshResponses = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/workstream/respond/${encodeURIComponent(token)}/load`, { method: 'GET', credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        setResponses(data.responses || []);
        if (data.outreach) setOutreach(data.outreach);
      }
    } catch {}
  }, [token]);

  if (phase === 'arrival') {
    return <Arrival onContinue={load} token={token} />;
  }
  if (phase === 'loading') {
    return <FullScreenLoader />;
  }
  if (phase === 'error') {
    return <ErrorScreen message={error} />;
  }
  if (phase === 'done') {
    return <DoneScreen outreach={outreach} />;
  }
  return (
    <ItemsView
      token={token}
      outreach={outreach}
      items={items}
      responses={responses}
      onActioned={refreshResponses}
      onAllAcknowledged={() => setPhase('done')}
    />
  );
}

// ===========================================================================
// 1. Arrival — defeats Outlook Safelinks pre-fetch
// ===========================================================================

function Arrival({ onContinue, token }) {
  return (
    <Shell>
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-orange-100 to-amber-100 mb-6">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-2">You have follow-up items</h1>
        <p className="text-slate-600 mb-8 max-w-md mx-auto">
          A compliance team member has sent you a quick follow-up. Click below to see what they need and respond — no login required.
        </p>
        <button
          type="button"
          onClick={onContinue}
          className="px-7 py-3 bg-gradient-to-r from-orange-600 to-amber-500 rounded-xl font-semibold text-white text-base shadow-lg shadow-orange-500/25 hover:shadow-xl hover:shadow-orange-500/30 transition"
        >
          Continue
        </button>
        <p className="text-[11px] text-slate-400 mt-6">
          This link is private to you. Please don't forward it.
        </p>
      </div>
    </Shell>
  );
}

// ===========================================================================
// 2. Items view
// ===========================================================================

function ItemsView({ token, outreach, items, responses, onActioned, onAllAcknowledged }) {
  const [selected, setSelected] = useState(new Set());
  const [busyItemId, setBusyItemId] = useState(null);
  const [busyBulk, setBusyBulk] = useState(false);
  const [toast, setToast] = useState(null);

  // Map: itemId → array of responses (most recent first)
  const responseMap = useMemo(() => {
    const m = new Map();
    for (const r of responses) {
      if (!m.has(r.cart_item_id)) m.set(r.cart_item_id, []);
      m.get(r.cart_item_id).push(r);
    }
    return m;
  }, [responses]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const performItem = async (itemId, path, body) => {
    setBusyItemId(itemId);
    try {
      const res = await fetch(`${API}/api/workstream/respond/${encodeURIComponent(token)}/items/${itemId}/${path}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {}),
      });
      const data = await res.json();
      if (data.success) {
        await onActioned();
        showToast(messageFor(path));
      } else {
        showToast('Couldn’t save: ' + (data.message || 'unknown error'));
      }
    } catch (e) {
      showToast('Network error');
    } finally {
      setBusyItemId(null);
    }
  };

  const acknowledgeAll = async () => {
    setBusyBulk(true);
    try {
      const res = await fetch(`${API}/api/workstream/respond/${encodeURIComponent(token)}/acknowledge-all`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json();
      if (data.success) {
        onAllAcknowledged();
      } else {
        showToast('Couldn’t acknowledge: ' + (data.message || ''));
      }
    } catch (e) {
      showToast('Network error');
    } finally {
      setBusyBulk(false);
    }
  };

  const acknowledgeSelected = async () => {
    setBusyBulk(true);
    let count = 0;
    for (const id of selected) {
      try {
        const res = await fetch(`${API}/api/workstream/respond/${encodeURIComponent(token)}/items/${id}/acknowledge`, {
          method: 'POST', credentials: 'include',
        });
        if (res.ok) count += 1;
      } catch {}
    }
    setBusyBulk(false);
    setSelected(new Set());
    await onActioned();
    showToast(`Acknowledged ${count} item${count === 1 ? '' : 's'}`);
  };

  return (
    <Shell>
      <SenderBanner outreach={outreach} />

      <div className="mt-6 mb-4 flex items-end justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900">{items.length} item{items.length === 1 ? '' : 's'} need your attention</h2>
          <p className="text-sm text-slate-500 mt-0.5">Take action below — your responses go straight back to {outreach?.sender_name || 'the sender'}.</p>
        </div>
        <button
          type="button"
          onClick={acknowledgeAll}
          disabled={busyBulk || items.length === 0}
          className="hidden sm:inline-flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition"
        >
          {busyBulk ? 'Acknowledging…' : `Acknowledge all (${items.length})`}
        </button>
      </div>

      <div className="space-y-3">
        {items.map((item) => (
          <ItemCard
            key={item.id}
            item={item}
            token={token}
            selected={selected.has(item.id)}
            onToggleSelect={() => toggleSelect(item.id)}
            responses={responseMap.get(item.id) || []}
            busy={busyItemId === item.id}
            onAck={() => performItem(item.id, 'acknowledge')}
            onMarkDone={() => performItem(item.id, 'mark-done')}
            onComment={(comment) => performItem(item.id, 'comment', { comment })}
            onSnooze={(snooze_until, reason) => performItem(item.id, 'snooze', { snooze_until, reason })}
            onActioned={onActioned}
          />
        ))}
      </div>

      {selected.size > 0 && (
        <StickyBar>
          <span className="text-sm text-slate-600">
            <strong className="text-slate-900">{selected.size}</strong> selected
          </span>
          <div className="flex-1" />
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            className="text-xs text-slate-500 hover:text-slate-700"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={acknowledgeSelected}
            disabled={busyBulk}
            className="px-4 py-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-sm font-semibold disabled:opacity-40 transition"
          >
            {busyBulk ? 'Working…' : `Acknowledge ${selected.size}`}
          </button>
        </StickyBar>
      )}

      {toast && <Toast message={toast} />}
    </Shell>
  );
}

// The whole ask, in one small form: "how many met the standard, out of how
// many total?" The engine judges instantly and the verdict shows right here —
// green when the benchmark is met, amber when it is not. Nothing else to
// learn, nowhere else to go.
function ResultEntry({ item, token }) {
  const meta = item.result_entry;
  const [num, setNum] = useState('');
  const [den, setDen] = useState('');
  const [busy, setBusy] = useState(false);
  const [verdict, setVerdict] = useState(null);
  const [err, setErr] = useState(null);
  if (!meta) return null;

  const periodLabel = (() => {
    const [y, m] = String(meta.period || '').split('-').map(Number);
    return y && m ? new Date(y, m - 1, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' }) : meta.period;
  })();

  const existing = meta.current && meta.current.denominator
    ? `${meta.current.numerator} of ${meta.current.denominator} (${meta.current.value}%)`
    : null;

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const res = await fetch(`${API}/api/workstream/respond/${encodeURIComponent(token)}/items/${item.id}/enter-result`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ numerator: Number(num), denominator: Number(den) }),
      });
      const data = await res.json();
      if (!data.success) { setErr(data.message || 'Could not save.'); setBusy(false); return; }
      setVerdict(data);
    } catch (e) { setErr('Could not save — please try again.'); }
    setBusy(false);
  };

  if (meta.locked) {
    return <p className="mt-3 text-sm text-slate-500">This period is closed — no entry needed.</p>;
  }

  if (verdict) {
    const met = verdict.monitoring_status === 'met';
    return (
      <div className={`mt-3 rounded-xl px-4 py-3 text-sm font-medium ${met ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-amber-50 text-amber-800 border border-amber-200'}`}>
        {met
          ? `${verdict.value}% — meets the ${verdict.benchmark}% benchmark. Thank you, you're done with this one.`
          : `${verdict.value}% — below the ${verdict.benchmark}% benchmark. Recorded; compliance will follow up on next steps.`}
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
      <p className="text-sm font-medium text-slate-800">
        {meta.measure}
        {meta.benchmark !== null && <span className="text-slate-500 font-normal"> · benchmark {meta.benchmark}%</span>}
      </p>
      <p className="text-xs text-slate-500 mt-0.5">Enter the result for <strong>{periodLabel}</strong>{existing ? ` — currently recorded: ${existing}` : ''}</p>
      <div className="flex items-center gap-2 mt-3 flex-wrap">
        <input type="number" min="0" inputMode="numeric" value={num} onChange={(e) => setNum(e.target.value)}
          placeholder="Met the standard" className="w-40 rounded-lg border-slate-300 text-sm py-2" />
        <span className="text-sm text-slate-500">out of</span>
        <input type="number" min="1" inputMode="numeric" value={den} onChange={(e) => setDen(e.target.value)}
          placeholder="Total" className="w-28 rounded-lg border-slate-300 text-sm py-2" />
        <button type="button" onClick={submit}
          disabled={busy || !num || !den || Number(num) > Number(den)}
          className="px-4 py-2 rounded-lg bg-gradient-to-r from-orange-600 to-amber-500 text-white text-sm font-semibold disabled:opacity-50 hover:opacity-90">
          {busy ? 'Saving…' : 'Submit'}
        </button>
      </div>
      {num && den && Number(den) > 0 && Number(num) <= Number(den) && (
        <p className="text-xs text-slate-500 mt-2">That's {Math.round((Number(num) / Number(den)) * 1000) / 10}%.</p>
      )}
      {err && <p className="text-xs text-rose-600 mt-2">{err}</p>}
    </div>
  );
}

function ItemCard({ item, token, selected, onToggleSelect, responses, busy, onAck, onMarkDone, onComment, onSnooze, onActioned }) {
  const [commentOpen, setCommentOpen] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [snoozeDate, setSnoozeDate] = useState(defaultSnoozeDate());
  const [snoozeReason, setSnoozeReason] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState(null);
  const fileInputRef = useRef(null);

  const uploadFile = async (file) => {
    if (!file || uploading) return;
    setUploading(true); setUploadMsg(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/api/workstream/respond/${encodeURIComponent(token)}/items/${item.id}/attach`, {
        method: 'POST', body: fd,
      });
      const raw = await res.text();
      let data = null; try { data = JSON.parse(raw); } catch { /* non-JSON */ }
      if (data?.success) {
        setUploadMsg(`Attached ${data.file_name} · saved to the item`);
        if (onActioned) await onActioned();
      } else {
        setUploadMsg(`Couldn’t attach: ${data?.message || 'upload failed'}`);
      }
    } catch { setUploadMsg('Network error while uploading'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const lastAction = responses[0];
  const isAcked = responses.some((r) => r.action === 'acknowledged');
  const isDone  = responses.some((r) => r.action === 'marked_done');
  const isSnoozed = responses.some((r) => r.action === 'snoozed');

  let stateBadge = null;
  if (isDone)      stateBadge = <Badge tone="success">Marked done</Badge>;
  else if (isAcked) stateBadge = <Badge tone="info">Acknowledged</Badge>;
  else if (isSnoozed) stateBadge = <Badge tone="warn">Snoozed</Badge>;

  return (
    <div className={`rounded-2xl border bg-white transition ${selected ? 'border-orange-400 ring-2 ring-orange-200' : 'border-slate-200 hover:border-slate-300'}`}>
      <div className="p-5">
        <div className="flex items-start gap-3 mb-3">
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelect}
            className="mt-1 w-4 h-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
            aria-label="Select item"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{moduleLabel(item.source_module)}</span>
              {item.due_date && <span className="text-[11px] text-slate-500">due {fmtDate(item.due_date)}</span>}
              {stateBadge}
            </div>
            <h3 className="text-base font-semibold text-slate-900 leading-snug">{item.title}</h3>
            <ResultEntry item={item} token={token} />
          </div>
          {item.deep_link && (
            <a
              href={item.deep_link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-semibold text-orange-700 hover:text-orange-800 whitespace-nowrap"
            >
              {item.source_module === 'mandate_action' ? 'Open in Mandate' : item.source_module === 'readiness_item' ? 'Open in Readiness' : 'Open in PSI'}
            </a>
          )}
        </div>

        {/* Quick actions */}
        <div className="flex flex-wrap gap-2 mt-3">
          <ActionButton onClick={onAck} disabled={busy || isAcked} primary>
            {isAcked ? 'Acknowledged' : 'Acknowledge'}
          </ActionButton>
          <ActionButton onClick={onMarkDone} disabled={busy || isDone}>
            {isDone ? 'Done' : 'Mark done'}
          </ActionButton>
          <ActionButton onClick={() => setCommentOpen((v) => !v)} disabled={busy}>
            Comment
          </ActionButton>
          <ActionButton onClick={() => setSnoozeOpen((v) => !v)} disabled={busy}>
            Snooze
          </ActionButton>
          {(item.source_module === 'mandate_action' || item.source_module === 'readiness_item') && (
            <>
              <input ref={fileInputRef} type="file" className="hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.eml,.msg"
                onChange={(e) => uploadFile(e.target.files?.[0])} />
              <ActionButton onClick={() => fileInputRef.current?.click()} disabled={busy || uploading}>
                {uploading ? 'Attaching…' : 'Attach file'}
              </ActionButton>
            </>
          )}
        </div>
        {uploadMsg && <div className="mt-2 text-xs text-slate-600">{uploadMsg}</div>}

        {/* Comment composer */}
        {commentOpen && (
          <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
            <textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Type a note back to the compliance team…"
              rows={3}
              maxLength={2000}
              className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 focus:border-orange-500 focus:outline-none resize-none"
            />
            <div className="flex justify-end gap-2 mt-2">
              <button type="button" onClick={() => { setCommentOpen(false); setCommentText(''); }} className="px-3 py-1.5 text-xs text-slate-500 hover:text-slate-700">Cancel</button>
              <button
                type="button"
                disabled={!commentText.trim() || busy}
                onClick={() => { onComment(commentText.trim()); setCommentOpen(false); setCommentText(''); }}
                className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-md text-xs font-semibold disabled:opacity-40"
              >
                Send comment
              </button>
            </div>
          </div>
        )}

        {/* Snooze composer */}
        {snoozeOpen && (
          <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
            <div className="flex gap-2 mb-2">
              <input
                type="date"
                value={snoozeDate}
                min={tomorrowYmd()}
                onChange={(e) => setSnoozeDate(e.target.value)}
                className="bg-white border border-amber-200 rounded-md px-2 py-1.5 text-sm text-slate-800 focus:border-amber-500 focus:outline-none"
              />
              <input
                type="text"
                value={snoozeReason}
                onChange={(e) => setSnoozeReason(e.target.value)}
                placeholder="Why?  (optional but appreciated)"
                maxLength={500}
                className="flex-1 bg-white border border-amber-200 rounded-md px-3 py-1.5 text-sm text-slate-800 focus:border-amber-500 focus:outline-none"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setSnoozeOpen(false)} className="px-3 py-1.5 text-xs text-slate-500 hover:text-slate-700">Cancel</button>
              <button
                type="button"
                disabled={busy}
                onClick={() => { onSnooze(snoozeDate, snoozeReason); setSnoozeOpen(false); }}
                className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-md text-xs font-semibold disabled:opacity-40"
              >
                Snooze until {fmtDate(snoozeDate)}
              </button>
            </div>
          </div>
        )}

        {/* Recent responses (audit trail-ish, recipient-visible) */}
        {responses.length > 0 && (
          <div className="mt-3 pl-3 border-l-2 border-slate-100">
            {responses.slice(0, 3).map((r) => (
              <div key={r.id} className="text-xs text-slate-500 py-1">
                <span className="text-slate-400">{fmtRelative(r.created_at)} —</span>{' '}
                <span className="text-slate-700">{verbForAction(r.action)}</span>
                {r.action === 'commented' && r.comment && <span className="text-slate-500"> · "{truncate(r.comment, 80)}"</span>}
                {r.action === 'snoozed' && r.snooze_until && <span className="text-slate-500"> until {fmtDate(r.snooze_until)}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// 3. Done
// ===========================================================================

function DoneScreen({ outreach }) {
  return (
    <Shell>
      <div className="text-center py-16">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-emerald-100 mb-6">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-2">All set</h1>
        <p className="text-slate-600 mb-6 max-w-md mx-auto">
          {outreach?.sender_name || 'The compliance team'} has been notified. You can close this window.
        </p>
      </div>
    </Shell>
  );
}

function ErrorScreen({ message }) {
  return (
    <Shell>
      <div className="text-center py-16">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-red-100 mb-6">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#b91c1c" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /><circle cx="12" cy="12" r="10"/>
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Link unavailable</h1>
        <p className="text-slate-600 mb-6 max-w-md mx-auto">{message || 'This link is no longer valid.'}</p>
        <p className="text-sm text-slate-500">
          If this is unexpected, ask the sender to resend the follow-up.
        </p>
      </div>
    </Shell>
  );
}

// ===========================================================================
// Small reusable bits
// ===========================================================================

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-2xl mx-auto px-4 py-10 sm:py-16">
        <div className="text-center mb-6">
          <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Precision Compliance Group · Workstream</p>
        </div>
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-6 sm:p-8">
          {children}
        </div>
      </div>
    </div>
  );
}

function SenderBanner({ outreach }) {
  if (!outreach) return null;
  const name = outreach.sender_name || outreach.sender_email;
  return (
    <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 text-white font-bold flex items-center justify-center text-sm">
        {initials(name)}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900 truncate">{name}</p>
        <p className="text-xs text-slate-500 truncate">{outreach.subject}</p>
      </div>
      <span className="text-[11px] text-slate-400 hidden sm:inline">{fmtDate(outreach.sent_at)}</span>
    </div>
  );
}

function ActionButton({ children, onClick, disabled, primary }) {
  const base = 'px-3 py-1.5 rounded-lg text-xs font-semibold transition border';
  const cls = primary
    ? 'bg-gradient-to-r from-orange-600 to-amber-500 hover:opacity-90 text-white border-transparent'
    : 'bg-white hover:bg-slate-50 text-slate-700 border-slate-200';
  return (
    <button type="button" onClick={onClick} disabled={disabled} className={`${base} ${cls} disabled:opacity-40 disabled:cursor-not-allowed`}>
      {children}
    </button>
  );
}

function Badge({ tone, children }) {
  const cls = tone === 'success' ? 'bg-emerald-100 text-emerald-700'
            : tone === 'info'    ? 'bg-blue-100 text-blue-700'
            : tone === 'warn'    ? 'bg-amber-100 text-amber-700'
            : 'bg-slate-100 text-slate-700';
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${cls}`}>{children}</span>;
}

function StickyBar({ children }) {
  return (
    <div className="fixed inset-x-0 bottom-0 z-30 p-4 pointer-events-none">
      <div className="max-w-xl mx-auto bg-white border border-slate-200 rounded-2xl shadow-2xl px-4 py-3 flex items-center gap-3 pointer-events-auto">
        {children}
      </div>
    </div>
  );
}

function Toast({ message }) {
  return (
    <div className="fixed inset-x-0 top-6 z-40 flex justify-center pointer-events-none">
      <div className="bg-slate-900 text-white text-sm px-4 py-2 rounded-full shadow-lg pointer-events-auto">{message}</div>
    </div>
  );
}

function FullScreenLoader() {
  return (
    <Shell>
      <div className="py-16 text-center">
        <div className="w-10 h-10 mx-auto border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function initials(s) {
  const parts = String(s || '?').trim().split(/\s+/).slice(0, 2);
  return parts.map((w) => w[0]).join('').toUpperCase() || '?';
}

function fmtDate(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return String(d); }
}

function fmtRelative(d) {
  if (!d) return '';
  const ms = Date.now() - new Date(d).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  return `${days}d ago`;
}

function moduleLabel(m) {
  return ({
    risk: 'Risk', cap: 'CAP', cap_milestone: 'CAP Milestone',
    fdr_finding: 'FDR Finding', na_finding: 'Network Adequacy',
    privacy_incident: 'Privacy Incident', ppm_policy: 'Policy',
    ppm_recommendation: 'Policy Recommendation', ppm_drift: 'Policy Drift',
    coa_activity: 'COA Activity', calendar_obligation: 'Obligation',
    compliance_issue: 'Issue', mandate_action: 'Mandate Action',
  })[m] || String(m || '').replace(/_/g, ' ');
}

function verbForAction(a) {
  return a === 'acknowledged' ? 'Acknowledged'
       : a === 'marked_done'  ? 'Marked done'
       : a === 'commented'    ? 'Commented'
       : a === 'snoozed'      ? 'Snoozed'
       : a;
}

function messageFor(p) {
  return p === 'acknowledge' ? 'Acknowledged · sent back to Compliance'
       : p === 'mark-done'   ? 'Marked done · sent back to Compliance'
       : p === 'comment'     ? 'Comment sent'
       : p === 'snooze'      ? 'Snoozed · we’ll remind you'
       : 'Saved';
}

function defaultSnoozeDate() {
  const d = new Date(); d.setDate(d.getDate() + 3);
  return d.toISOString().slice(0, 10);
}

function tomorrowYmd() {
  const d = new Date(); d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

function truncate(s, n) {
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}
